import { ColumnEditorOptions } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { ChangeEvent, FocusEvent, KeyboardEvent, useRef, useState } from "react";
import {
    parseCalendarDate,
    toCalendarDate
} from "@idwal/checklist-visibility";

interface DateEditorProps {
    options: ColumnEditorOptions;
}

// Chrome's year segment accepts six digits unless max caps it at four.
const LATEST_DATE = "9999-12-31";

// Chrome reports a full date after every year digit, so without a floor a
// half-typed year such as 0026 would be saved.
const EARLIEST_DATE = "1900-01-01";

const CELL_COMMIT_KEYS = new Set(["Enter", "NumpadEnter", "Tab"]);

// DataTable refocuses its cell's first input after every render, and Chrome
// drops a half-typed date segment whenever the input is focused again.
const ignoreRefocus = (input: HTMLInputElement | null) => {
    if (!input) return;

    input.focus = (focusOptions?: FocusOptions) => {
        if (input !== document.activeElement) {
            HTMLInputElement.prototype.focus.call(input, focusOptions);
        }
    };
};

// Format a stored calendar date for the reviewer, who reads dd/mm/yyyy.
const formatDisplayDate = (date: Date | null): string => {
    if (!date) return "";

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");

    return `${day}/${month}/${date.getFullYear()}`;
};

export const DateEditor = ({ options }: DateEditorProps) => {
    // Seed once so the existing date shows on open and survives re-renders.
    const [original] = useState(() => options.value);
    const [value, setValue] = useState(() => toCalendarDate(original) ?? "");
    const [invalid, setInvalid] = useState(false);
    const tabPressed = useRef(false);
    // The cell starts out holding the original and re-renders on every
    // report, so only an answer that differs from the last one is sent.
    const lastReport = useRef<unknown>(original);

    const report = (answer: unknown) => {
        if (answer === lastReport.current) return;

        lastReport.current = answer;
        options.editorCallback?.(answer);
    };

    const readInput = (input: HTMLInputElement) => {
        const { value: next, validity } = input;
        // A native date input reports "" whether some or all segments are
        // empty; only badInput tells a half-typed date from a cleared one.
        const isInvalid = next ? validity.rangeUnderflow : validity.badInput;

        setValue(next);
        setInvalid(isInvalid);

        if (isInvalid) {
            report(original);
        } else if (next) {
            report(next);
        } else {
            report(toCalendarDate(original) ? null : original);
        }

        return isInvalid;
    };

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        readInput(event.target);
    };

    // Clearing the last segment, or part-typing an empty date, leaves the
    // value at "" and so fires no change event.
    const handleKeyUp = (event: KeyboardEvent<HTMLInputElement>) => {
        readInput(event.currentTarget);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (!CELL_COMMIT_KEYS.has(event.code)) return;

        // Enter or Tab can commit the cell before the previous key's keyup
        // has been read.
        const isInvalid = readInput(event.currentTarget);

        if (event.code === "Tab") {
            // DataTable commits on Tab, which would stop Tab moving between
            // the date's segments.
            event.stopPropagation();
            tabPressed.current = true;
            setTimeout(() => {
                tabPressed.current = false;
            });
        } else if (isInvalid) {
            // PrimeReact 10.3.3 runs cellEditValidator only on Escape, so the
            // editor itself stops an invalid date being committed.
            event.preventDefault();
            event.stopPropagation();
        }
    };

    // Editors get no way to commit their cell, so the Tab that left the input
    // is replayed on the cell for DataTable to commit as usual.
    const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
        if (!tabPressed.current) return;

        tabPressed.current = false;
        event.currentTarget.closest("td")?.dispatchEvent(
            new window.KeyboardEvent("keydown", { key: "Tab", code: "Tab", bubbles: true })
        );
    };

    return (
        <InputText
            ref={ignoreRefocus}
            type="date"
            min={EARLIEST_DATE}
            max={LATEST_DATE}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onKeyUp={handleKeyUp}
            onBlur={handleBlur}
            className={invalid ? "p-invalid" : undefined}
            aria-invalid={invalid}
            autoFocus
        />
    );
};

export const dateDisplayTemplate = (value: unknown): string => {
    if (!value) return "";

    const date = parseCalendarDate(value);
    if (!date) return String(value);

    return formatDisplayDate(date);
};
